import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckCircle, FiLock, FiLoader, FiAlertCircle, FiMail } = FiIcons;

const BookingForm = () => {
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [formData, setFormData] = useState({
    name: '',
    business: '',
    email: '',
    phone: '',
    url: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const response = await fetch("https://formsubmit.co/ajax/theinkedmarketer@gmail.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          ...formData,
          _subject: `New Audit Request: ${formData.business}`,
          _template: "table",
          _captcha: "false"
        })
      });

      if (response.ok) {
        setStatus('success');
      } else {
        console.error("FormSubmit Error:", await response.text());
        setStatus('error');
      }
    } catch (error) {
      console.error("Submission Error:", error);
      setStatus('error');
    }
  };

  const mailtoLink = `mailto:theinkedmarketer@gmail.com?subject=New Audit Request - ${encodeURIComponent(formData.business)}&body=${encodeURIComponent(`Name: ${formData.name}\nBusiness: ${formData.business}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nWebsite: ${formData.url}\n\nPlease review my request for the Trust & Visibility Audit.`)}`;

  return (
    // Added overflow-hidden to prevent the background glow from causing scrollbars
    <section id="book" className="py-24 bg-navy-900 border-t border-white/5 relative overflow-hidden">
      {/* Glow effect */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-[600px] h-[600px] bg-teal/10 rounded-full blur-[100px] pointer-events-none"></div>
      
      <div className="max-w-3xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
            Request Your Audit
          </h2>
          <p className="text-xl text-slate-400">
            Fill out the details below to request your $750 Local Trust & Visibility Audit. I'll personally review your local presence and deliver your action plan.
          </p>
        </div>

        {status === 'success' && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card rounded-2xl p-12 text-center"
          >
            <div className="text-teal flex justify-center mb-6">
              <SafeIcon icon={FiCheckCircle} className="w-20 h-20" />
            </div>
            <h3 className="text-3xl font-extrabold text-white mb-4">Request Sent Successfully!</h3>
            <p className="text-slate-300 text-lg leading-relaxed">
              Thanks for reaching out. I'll review your details and follow up with next steps to begin your Trust & Visibility Audit.
            </p>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card rounded-2xl p-12 text-center border-rose-500/30"
          >
            <div className="text-rose-400 flex justify-center mb-6">
              <SafeIcon icon={FiAlertCircle} className="w-20 h-20" />
            </div>
            <h3 className="text-3xl font-extrabold text-white mb-4">Action Required</h3>
            <p className="text-slate-300 text-lg leading-relaxed mb-8">
              Our form server needs a moment to process this. Please click the button below to send your request directly to my inbox so we can get started immediately.
            </p>
            <a 
              href={mailtoLink}
              className="inline-flex justify-center items-center gap-2 bg-teal text-navy px-8 py-4 rounded-lg text-xl font-bold hover:bg-teal-400 transition-all shadow-[0_0_20px_rgba(0,212,200,0.2)]"
            >
              <SafeIcon icon={FiMail} />
              Send Request via Email
            </a>
          </motion.div>
        )}

        {(status === 'idle' || status === 'submitting') && (
          <form 
            onSubmit={handleSubmit} 
            className="glass-card rounded-2xl p-8 md:p-12 space-y-6"
          >
            <input type="text" name="_honey" style={{ display: 'none' }} />
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="name" className="block text-sm font-bold text-slate-300">Your Name *</label>
                <input required type="text" id="name" name="name" value={formData.name} onChange={handleChange} className="w-full px-5 py-4 rounded-lg bg-navy-800 border border-white/10 text-white focus:ring-2 focus:ring-teal focus:border-transparent outline-none transition-all" placeholder="John Doe" />
              </div>
              <div className="space-y-2">
                <label htmlFor="business" className="block text-sm font-bold text-slate-300">Business Name *</label>
                <input required type="text" id="business" name="business" value={formData.business} onChange={handleChange} className="w-full px-5 py-4 rounded-lg bg-navy-800 border border-white/10 text-white focus:ring-2 focus:ring-teal focus:border-transparent outline-none transition-all" placeholder="Apex Roofing" />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-bold text-slate-300">Work Email *</label>
              <input required type="email" id="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-5 py-4 rounded-lg bg-navy-800 border border-white/10 text-white focus:ring-2 focus:ring-teal focus:border-transparent outline-none transition-all" placeholder="john@example.com" />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="phone" className="block text-sm font-bold text-slate-300">Phone (Optional)</label>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-5 py-4 rounded-lg bg-navy-800 border border-white/10 text-white focus:ring-2 focus:ring-teal focus:border-transparent outline-none transition-all" placeholder="(555) 123-4567" />
              </div>
              <div className="space-y-2">
                <label htmlFor="url" className="block text-sm font-bold text-slate-300">Website URL (Optional)</label>
                <input type="url" id="url" name="url" value={formData.url} onChange={handleChange} className="w-full px-5 py-4 rounded-lg bg-navy-800 border border-white/10 text-white focus:ring-2 focus:ring-teal focus:border-transparent outline-none transition-all" placeholder="https://www.example.com" />
              </div>
            </div>

            <div className="pt-6">
              <button 
                type="submit" 
                disabled={status === 'submitting'}
                className="w-full flex justify-center items-center gap-2 bg-teal text-navy px-8 py-5 rounded-lg text-xl font-bold hover:bg-teal-400 transition-all shadow-[0_0_20px_rgba(0,212,200,0.2)] hover:shadow-[0_0_30px_rgba(0,212,200,0.4)] transform hover:-translate-y-1 disabled:opacity-70 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? (
                  <>
                    <SafeIcon icon={FiLoader} className="animate-spin text-2xl" />
                    Sending Request...
                  </>
                ) : (
                  "Request Audit ($750)"
                )}
              </button>
            </div>
            
            <div className="flex items-center justify-center gap-2 text-slate-500 text-sm mt-4">
              <SafeIcon icon={FiLock} />
              <span>Your information is 100% secure and will never be shared.</span>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default BookingForm;